import React, { useCallback, useState } from "react";
import { api } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Palette } from "lucide-react";
import { toast } from "sonner";

export default function Branding() {
  const fetcher = useCallback(() => api.get("/company/branding").then((r) => r.data), []);
  const { data, loading, reload } = useFetch(fetcher);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (data && !form) setForm(data);
  }, [data, form]);

  const update = (key, value) => setForm((f) => ({ ...(f || {}), [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company/branding", form);
      toast.success("Branding saved");
      reload();
    } catch (err) {
      console.error(err);
      toast.error("Could not save branding");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="branding-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Phase 1</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">Company branding</h1>
        <p className="text-slate-600 mt-2 max-w-xl">Set the company name, logo link and report footer used by HAVS Guard exports and dashboards.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200 space-y-5">
          <div className="space-y-2">
            <Label>Company name</Label>
            <Input value={form.company_name || ""} onChange={(e) => update("company_name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Logo URL</Label>
            <Input placeholder="https://example.com/logo.png" value={form.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} />
            <p className="text-xs text-slate-500">Use a hosted PNG/JPG logo URL. File upload can be added later with cloud storage.</p>
          </div>
          <div className="space-y-2">
            <Label>Accent colour</Label>
            <div className="flex gap-2">
              <Input type="color" className="w-16 p-1" value={form.accent_colour || "#0f172a"} onChange={(e) => update("accent_colour", e.target.value)} />
              <Input value={form.accent_colour || "#0f172a"} onChange={(e) => update("accent_colour", e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Report footer</Label>
            <Input placeholder="Prepared by HAVS Guard" value={form.report_footer || ""} onChange={(e) => update("report_footer", e.target.value)} />
          </div>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{saving ? "Saving…" : "Save branding"}</Button>
        </Card>

        <Card className="p-6 border-slate-200">
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <div className="p-5 text-white" style={{ background: form.accent_colour || "#0f172a" }}>
              <div className="flex items-center gap-3">
                {form.logo_url ? <img src={form.logo_url} alt="Logo preview" className="h-12 w-12 rounded bg-white object-contain p-1" /> : <div className="h-12 w-12 rounded bg-white/20 flex items-center justify-center"><Palette className="h-6 w-6" /></div>}
                <div>
                  <p className="text-xs uppercase tracking-widest opacity-80">HAVS Guard report</p>
                  <h2 className="font-display text-2xl font-bold">{form.company_name || "Your Company"}</h2>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">Employee exposure report preview</p>
              <div className="h-3 rounded bg-slate-100" />
              <div className="h-3 rounded bg-slate-100 w-2/3" />
              <p className="pt-4 border-t border-slate-100 text-xs text-slate-500">{form.report_footer || "Report footer appears here."}</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
